import { Link } from "react-router";

import type { UserDto } from "@/api/types";
import { buttonVariants } from "@/design-system/Button";
import { twResolve } from "@/helpers/tw-resolve";
import { useSlug } from "@/hooks/useSlug";
import { useForbiddenPopupContext } from "@/providers/ForbiddenPopupProvider";
import { routes } from "@/routes";

type UserNameLinkProps = {
  user: UserDto;
  size?: "sm" | "md";
  "data-testid"?: string;
};

export function UserFullNameLink({ user, size = "md", "data-testid": dataTestId }: UserNameLinkProps): React.ReactNode {
  const slug = useSlug();
  const forbiddenContext = useForbiddenPopupContext();

  if (user.canViewProfile) {
    return (
      <Link className="truncate" to={routes.users.details({ slug, id: user.id })} data-testid={dataTestId}>
        <UserFullName user={user} size={size} />
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={twResolve(
        buttonVariants({ variant: "ghostSecondary", size: "sm", inset: "compact" }),
        "h-auto min-w-0 truncate px-0",
      )}
      onClick={forbiddenContext.open}
      data-testid={dataTestId}
    >
      <UserFullName user={user} size={size} />
    </button>
  );
}

type UserFullNameProps = {
  user: UserDto;
  size?: "sm" | "md";
};

export function UserFullName({ user, size = "md" }: UserFullNameProps): React.ReactNode {
  return (
    <span
      data-testid="user-fullname"
      className={twResolve(
        "block truncate text-text-primary",
        user.deletedAt && "text-text-secondary",
        size === "sm" && "text-caption-emphasis",
        size === "md" && "text-body-emphasis",
      )}
    >
      {user.fullName}
    </span>
  );
}
